import { useState, useEffect } from 'react';
import { Shield, CheckCircle, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';

const ProofsTab = () => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProofs = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/my-proofs`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
        setProofs(res.data.proofs || []);
      } catch (error) {
        console.error('Error fetching proofs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProofs();
  }, []);
  
  const handleDownload = async (proofId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/my-proofs/${proofId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob',
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'proof.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur téléchargement');
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Shield className="w-6 h-6 text-success" />Mes Preuves de Résiliation
        </h2>
        <p className="text-gray-400 font-mono text-sm">// CONFIRMATIONS_OFFICIELLES</p>
      </div>
      
      {proofs.length === 0 ? (
        <div className="glass-card p-8 md:p-12 border border-gray-800 text-center">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-orbitron text-lg mb-2">Aucune preuve disponible</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Les confirmations de résiliation apparaîtront ici une fois que L'Effaceur aura reçu les réponses des créanciers.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {proofs.map((proof) => (
            <div key={proof.proof_id} className="glass-card p-4 md:p-6 border border-success/30 bg-success/5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-success/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{proof.creditor}</p>
                    <p className="text-success text-sm font-orbitron">Résiliation Confirmée</p>
                    <p className="text-gray-500 text-xs mt-1">
                      Confirmé le {new Date(proof.uploaded_at).toLocaleDateString('fr-FR')}
                    </p>
                    {proof.notes && (
                      <p className="text-gray-400 text-sm mt-2 italic">{proof.notes}</p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => handleDownload(proof.proof_id, proof.filename)}
                  className="bg-success text-black hover:bg-success/80 w-full md:w-auto"
                  data-testid={`download-proof-${proof.proof_id}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="glass-card p-4 border border-gray-700 bg-gray-900/30">
        <p className="text-gray-500 text-xs font-mono text-center">
          // Ces documents constituent des preuves légales de vos résiliations. Conservez-les précieusement.
        </p>
      </div>
    </div>
  );
};

export { ProofsTab };
